'use client'

import { type AxiosError, type AxiosResponse } from 'axios'
import useSWRMutation, { type SWRMutationResponse } from 'swr/mutation'

import { type LoginOtpConfirmRequestV3 } from '@redteclab/api/clients/bully'

import { buildHomeOneApiAxiosInstance } from '../../../api'
import {
  type PublicGlobalConfig,
  useGlobalConfigContext,
} from '../../../global-config'
import { gtmDataLayerPushLogin } from '../../../gtm'
import { HTTP_STATUS_CODE_BAD_REQUEST } from '../../../http'
import { urlResolverGetOneTimePasswordConfirmApiEndpoint } from '../../../url-handling'

import { refetchSwrsWhenUserLogsIn } from './useUserApiLoginMutation'

const postOneTimePasswordLoginData =
  (globalConfig: PublicGlobalConfig) =>
  async (
    swrKey: string,
    data: { arg: LoginOtpConfirmRequestV3 },
  ): Promise<AxiosResponse> => {
    return buildHomeOneApiAxiosInstance(globalConfig).post(
      urlResolverGetOneTimePasswordConfirmApiEndpoint(globalConfig),
      data.arg,
      {
        headers: {
          ...(process.env.NEXT_PUBLIC_SKIP_FRIENDLY_CAPTCHA === 'true' && {
            'skip-friendly-captcha-verification': 'true',
          }),
        },
      },
    )
  }

const getOneTimePasswordApiLoginMutationKey = (): string => {
  return 'useOneTimePasswordApiLoginMutation'
}

const GTM_LOGIN_WITH_OTP_RESPONSE_ERROR_CODE_WRONG_USER_OR_OTP = 1
const GTM_LOGIN_WITH_OTP_RESPONSE_ERROR_CODE_GENERAL = 2
const pushLoginWithOtpGtmErrorEvent = (error: AxiosError): void => {
  if (error.response?.status === HTTP_STATUS_CODE_BAD_REQUEST) {
    gtmDataLayerPushLogin({
      gtmLoginStatusCode:
        GTM_LOGIN_WITH_OTP_RESPONSE_ERROR_CODE_WRONG_USER_OR_OTP,
      loginActionType: 'loginWithOtp',
    })
  } else {
    gtmDataLayerPushLogin({
      gtmLoginStatusCode: GTM_LOGIN_WITH_OTP_RESPONSE_ERROR_CODE_GENERAL,
      loginActionType: 'loginWithOtp',
    })
  }
}

export const useOneTimePasswordApiLoginMutation = ({
  onError,
  onSuccess,
}: {
  onError: (err: AxiosError) => void
  onSuccess?: (
    afterLoginSideEffectsPromise: Promise<string | undefined>,
  ) => void
}): SWRMutationResponse<
  AxiosResponse,
  AxiosError,
  string,
  LoginOtpConfirmRequestV3
> => {
  const globalConfig = useGlobalConfigContext()

  return useSWRMutation(
    getOneTimePasswordApiLoginMutationKey(),
    postOneTimePasswordLoginData(globalConfig),
    {
      onError: (error: AxiosError) => {
        pushLoginWithOtpGtmErrorEvent(error)
        onError(error)
      },
      onSuccess: () => {
        onSuccess?.(refetchSwrsWhenUserLogsIn(globalConfig, 'loginWithOtp'))
      },
    },
  )
}
